import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {createFreeformQuestion} from '../../../../helpers/models/schedulingLinkHelpers';
import {SchedulingLinkQuestionModel} from '../../../../models/api/schedulingLinkQuestionModel';
import {SchedulingLinkQuestion} from './schedulingLinkQuestion';

/*
 * Props.
 */

export interface SchedulingLinkMeetingQuestionsProps {
  questions: ReadonlyArray<SchedulingLinkQuestionModel>;
  onQuestionsChange: HandlerOf<ReadonlyArray<SchedulingLinkQuestionModel>>;
  invalidQuestionOptionUids: ReadonlyArray<string>;
  setInvalidQuestionOptionUids: HandlerOf<ReadonlyArray<string>>;
}

/*
 * Style.
 */

const StyledAddQuestionButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 10px;
`;

const StyledQuestionsWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 10px;
`;

/*
 * Component.
 */

export const SchedulingLinkMeetingQuestions: FC<SchedulingLinkMeetingQuestionsProps> = (props) => {
  const {questions, onQuestionsChange, invalidQuestionOptionUids, setInvalidQuestionOptionUids} = props;

  const onAddQuestionClick = useCallback(
    () => onQuestionsChange([...questions, createFreeformQuestion()]),
    [onQuestionsChange, questions],
  );
  const onUpdateQuestion = useCallback(
    (targetIndex: number, newValue: SchedulingLinkQuestionModel) => {
      const newQuestions = questions.map((question, index) => (index === targetIndex ? newValue : question));
      onQuestionsChange(newQuestions);
    },
    [questions, onQuestionsChange],
  );
  const onRemoveQuestion = useCallback(
    (targetIndex: number) => {
      const newQuestions = questions.filter((_question, index) => index !== targetIndex);
      onQuestionsChange(newQuestions);
    },
    [questions, onQuestionsChange],
  );

  return (
    <>
      <StyledQuestionsWrapperDiv>
        {questions.map((question, index) => (
          <SchedulingLinkQuestion
            key={question.uid}
            question={question}
            onChange={(newQuestion) => onUpdateQuestion(index, newQuestion)}
            onRemove={() => onRemoveQuestion(index)}
            invalidQuestionOptionUids={invalidQuestionOptionUids}
            setInvalidQuestionOptionUids={setInvalidQuestionOptionUids}
          />
        ))}
      </StyledQuestionsWrapperDiv>
      <StyledAddQuestionButtonWrapperDiv>
        <Button size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.LINK} onClick={onAddQuestionClick}>
          <Trans id="schedulinglink-settings-addquestion">+ Add question</Trans>
        </Button>
      </StyledAddQuestionButtonWrapperDiv>
    </>
  );
};
